import { Injectable } from '@angular/core';
import { MatDialog } from '@angular/material/dialog';

import { DEFAULT_INTERRUPTSOURCES, Idle } from '@ng-idle/core';
import { DateTime } from 'luxon';
import { ToastRef, ToastrService } from 'ngx-toastr';
import { map, switchMap } from 'rxjs';

import { environment } from 'environments/environment';

import { AuthService } from '../../core/auth/auth.service';
import { ComponentBase } from '../component.base';
import { ConfirmationDialogComponent } from '../components/confirmation-dialog/confirmation-dialog.component';

@Injectable({
  providedIn: 'root',
})
export class InactivityMonitorService extends ComponentBase {
  toast: ToastRef<any>;

  constructor(
    private idle: Idle,
    private authService: AuthService,
    private toastr: ToastrService,
    private dialog: MatDialog,
  ) {
    super();

    idle.setIdle(this.inactivityMonitorIdle() * 60); // Timeout warning after x minutes of idle
    idle.setTimeout(this.inactivityMonitorTimeout()); // Timeout x seconds after warning
    idle.setInterrupts(DEFAULT_INTERRUPTSOURCES);

    idle.onIdleEnd.subscribe(() => this.hideTimeoutWarning());
    idle.onTimeoutWarning.subscribe((seconds) => this.showTimeoutWarning(seconds));
    idle.onTimeout.subscribe(() => this.timedOut());
  }

  inactivityMonitorIdle(): number {
    const parsedValue = Number(environment.inactivityMonitorIdle);
    return isNaN(parsedValue) ? 15 : parsedValue;
  }

  inactivityMonitorTimeout(): number {
    const parsedValue = Number(environment.inactivityMonitorTimeout);
    return isNaN(parsedValue) ? 30 : parsedValue;
  }

  start(): void {
    const nowTime = DateTime.now();
    const expiryTime = DateTime.fromMillis(+localStorage.getItem('ng2Idle.main.expiry'));

    this.authService.userProfile$.pipe(map((profile) => profile.auth_time)).subscribe((authTime) => {
      if (expiryTime < nowTime && authTime < expiryTime) {
        this.timedOut();
      } else {
        this.idle.watch();
      }
    });
  }

  stop(): void {
    this.idle.stop();
  }

  showTimeoutWarning(seconds: number): void {
    if (this.toast == null) {
      this.toast = this.toastr.show(
        'Move mouse or press any key to continue',
        'Your session is about to expire',
        {
          timeOut: seconds * 1000,
          disableTimeOut: 'extendedTimeOut',
          tapToDismiss: false,
        },
        'toast-warning toast-icon-clock',
      ).toastRef;
    }
  }

  hideTimeoutWarning() {
    if (this.toast != null) {
      this.toast.manualClose();
      this.toast = null;
    }
  }

  timedOut(): void {
    this.hideTimeoutWarning();

    const dialog = this.dialog.open(ConfirmationDialogComponent, {
      height: '300',
      width: '700',
      data: {
        title: 'Session expired',
        message: 'Your session has expired due to inactivity and you have been automatically logged out.',
        actions: {
          confirm: {
            show: true,
            color: 'accent',
            label: 'Login',
          },
        },
      },
      closeOnNavigation: false,
      disableClose: true,
    });

    dialog
      .afterClosed()
      .pipe(switchMap(() => this.authService.logoffAndRevokeTokens()))
      .subscribe();
  }
}
